import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

import { assertNever } from '../../../../../../utils/variables';
import { Cluster } from '../../../../../../viewer/elements/cluster';
import { Poi } from '../../../../../../viewer/elements/poi/poi';
import { ClusterLabel } from './labels/poi-labels/cluster-label';
import { PoiLabel } from './labels/poi-labels/poi-label';

interface LabelEntry {
  root: Root;
  css2d: CSS2DObject;
  div: HTMLDivElement;
}

const entries = new Map<string, LabelEntry>();

interface PoiLabelType {
  type: 'poi';
  poi: Poi;
}

interface ClusterLabelType {
  type: 'cluster';
  cluster: Cluster;
}

type LabelOptions = PoiLabelType | ClusterLabelType;

/**
 * Create a CSS2DObject for a POI label and mount the React component into it.
 */
export const createLabel = (labelOptions: LabelOptions): CSS2DObject => {
  const uuid = resolveLabelUuid(labelOptions);

  // If an entry already exists, remove it first
  removeLabel(labelOptions);

  // Create container DIV for React
  const div = document.createElement('div');

  // Mount React component
  const root = createRoot(div);
  root.render(resolveLabelComponent(labelOptions));

  // Wrap in CSS2DObject
  const css2d = new CSS2DObject(div);

  // Store entry for future updates/removal
  entries.set(uuid, { root, css2d, div });

  return css2d;
};

/**
 * Update an existing POI label's props.
 */
export const updateLabel = (poi: Poi): void => {
  const entry = entries.get(poi.uuid);
  if (!entry) {
    return;
  }

  // Re-render React with new props
  entry.root.render(<PoiLabel poi={poi} />);
};

/**
 * Remove and clean up a POI label.
 */
export const removeLabel = (labelType: LabelOptions): void => {
  const uuid = resolveLabelUuid(labelType);

  const entry = entries.get(uuid);
  if (!entry) {
    return;
  }

  // Unmount React
  entry.root.unmount();

  // Remove DOM element
  if (entry.div.parentElement) {
    entry.div.parentElement.removeChild(entry.div);
  }

  if (entry.css2d.parent) {
    entry.css2d.parent.remove(entry.css2d);
  }

  // Remove from map
  entries.delete(uuid);
};

const resolveLabelUuid = (labelType: LabelOptions) => {
  const type = labelType.type;
  switch (type) {
    case 'poi':
      return labelType.poi.uuid;
    case 'cluster':
      return labelType.cluster.uuid;
    default:
      assertNever(type);
  }
};

const resolveLabelComponent = (labelOptions: LabelOptions) => {
  const type = labelOptions.type;
  switch (type) {
    case 'poi':
      return <PoiLabel poi={labelOptions.poi} />;
    case 'cluster':
      return <ClusterLabel cluster={labelOptions.cluster} />;
    default:
      assertNever(type);
  }
};
